import { useMutation, useQuery, useQueryClient } from 'react-query';
import { toast } from 'sonner';
import { apiService } from '../../../../utils/api';

export interface Consultant {
  id: string;
  first_name: string;
  last_name: string;
  manufacturer: string;
  email: string | null;
  phone: string | null;
  note: string | null;
  company: string;
  is_active: boolean;
}

export interface ConsultantPayload {
  first_name: string;
  last_name: string;
  manufacturer: string;
  email?: string | null;
  phone?: string | null;
  note?: string | null;
}

const BASE = '/api/v1/consultant/';
export const CONSULTANT_KEY = 'consultant';

const showError = (err: any) => {
  const details = err?.response?.data?.details;
  const fieldError =
    details && typeof details === 'object' ? Object.values(details).flat()[0] : undefined;
  toast.error((fieldError as string) || err?.response?.data?.message || 'Operace se nezdařila');
};

export const useConsultantList = (search?: string) =>
  useQuery<Consultant[]>({
    queryKey: [CONSULTANT_KEY, 'list', search],
    queryFn: async () => {
      const { data } = await apiService.get(BASE, {
        params: { search: search || undefined },
      });
      return data;
    },
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  });

export const useConsultantDetail = (id?: string | null) =>
  useQuery<Consultant>({
    queryKey: [CONSULTANT_KEY, 'detail', id],
    queryFn: async () => {
      const { data } = await apiService.get(`${BASE}${id}/`);
      return data;
    },
    enabled: !!id,
    refetchOnWindowFocus: false,
  });

export interface ConsultantDropdownItem {
  id: string;
  title: string;
  is_active: boolean;
}

export const useConsultantDropdown = () =>
  useQuery<ConsultantDropdownItem[]>({
    queryKey: [CONSULTANT_KEY, 'dropdown'],
    queryFn: async () => {
      const { data } = await apiService.get(`${BASE}dropdown/`, {
        params: { pagination: false },
      });
      return data;
    },
    refetchOnWindowFocus: false,
  });

export const useCreateConsultant = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (values: ConsultantPayload) => apiService.post<Consultant>(BASE, values),
    onSuccess: () => {
      queryClient.invalidateQueries(CONSULTANT_KEY);
      toast.success('Konzultant vytvořen');
    },
    onError: showError,
  });
};

export const useUpdateConsultant = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, values }: { id: string; values: Partial<ConsultantPayload> }) =>
      apiService.patch<Consultant>(`${BASE}${id}/`, values),
    onSuccess: () => {
      queryClient.invalidateQueries(CONSULTANT_KEY);
      toast.success('Konzultant upraven');
    },
    onError: showError,
  });
};

export const useToggleConsultantActive = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      apiService.post(`${BASE}${id}/${isActive ? 'deactivate' : 'activate'}/`, {}),
    onSuccess: (_, { isActive }) => {
      queryClient.invalidateQueries(CONSULTANT_KEY);
      toast.success(isActive ? 'Konzultant deaktivován' : 'Konzultant aktivován');
    },
    onError: showError,
  });
};
